import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import DefaultLoader from "components/common/DefaultLoader";
import { makeTransactionOptions } from "components/inputs/program/transactionSupport";
import * as labels from "config.labels";
import { useMemo } from "react";
import * as staticDataService from "services/staticDataService";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const LOADING_KEY = "__LOADING__";

/**
 * Resolve the transaction choices for the current company from the
 * `transaction_types` config (`{ all, active }`), falling back to the
 * historical hard-coded list, exactly as the program-level dropdown does. A
 * stored `selected` value that is no longer offered is appended, so
 * restricting a company's options never blanks historic data.
 */
export const useTransactionChoices = (selected) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const {
    data: transactionTypesConfig,
    isLoading,
  } = staticDataService.useConfigQuery("transaction_types");
  const choices = useMemo(
    () =>
      makeTransactionOptions(transactionTypesConfig, selected).map(
        ({ key, name, label }) => ({
          key,
          name: label ? translateLabel(label, { default: name }) : name,
        })
      ),
    [transactionTypesConfig, selected, translateLabel]
  );
  return { choices, isLoading };
};

/**
 * Shared Transaction select, agnostic about whether it writes to the program
 * or to a layer.
 */
const TransactionType = ({ value, onChange, disabled, label }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const { choices, isLoading } = useTransactionChoices(value);

  return (
    <FormControl variant={"filled"} fullWidth>
      <InputLabel shrink={true}>
        {label ??
          translateLabel(labels.TRANSACTION, {
            default: "Transaction",
          })}
      </InputLabel>
      <Select
        value={isLoading ? LOADING_KEY : value ?? ""}
        onChange={(event) => {
          const selected = event?.target?.value;
          if (selected !== LOADING_KEY) {
            onChange(selected);
          }
        }}
        disabled={isLoading || disabled}
        fullWidth
      >
        {isLoading && (
          <MenuItem value={LOADING_KEY}>
            <DefaultLoader
              size={16}
              style={{
                display: "block",
                textAlign: "center",
              }}
            />
          </MenuItem>
        )}
        {choices.map(({ key, name }) => (
          <MenuItem key={key} value={key}>
            {name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default TransactionType;
